import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ExploreSearchProps {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}

/** Explore search bar — ported from the OG WINR (js/app.js:1448 + _exploreWireSearch:2473).
 *  Live filter, clear button, Esc to clear, ⌘K / Ctrl+K to focus. */
export function ExploreSearch({ value, onChange, className }: ExploreSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(Boolean(value));
  const expanded = open || Boolean(value);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
        window.requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
        return;
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        onChange('');
        setOpen(false);
        inputRef.current?.blur();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onChange]);

  return (
    <div className={cn('relative flex flex-col items-center gap-3.5', expanded ? className : 'w-10')}>
      <form
        className={cn(
          'relative flex h-10 items-center bg-transparent transition-all duration-300 focus-within:text-white',
          expanded
            ? 'w-full gap-3 rounded-pill border border-white/10 bg-white/[0.035] px-4 shadow-[0_12px_34px_rgba(0,0,0,0.22)]'
            : 'w-10 justify-center rounded-full',
        )}
        onSubmit={(e) => e.preventDefault()}
      >
        <button
          type="button"
          className="grid h-10 w-10 flex-none place-items-center bg-transparent text-accent transition-colors hover:text-hi"
          aria-label="Open search"
          onClick={() => {
            setOpen(true);
            window.requestAnimationFrame(() => inputRef.current?.focus());
          }}
        >
          <Search size={18} strokeWidth={2.3} />
        </button>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search communities, creators, topics..."
          autoComplete="off"
          className={cn(
            'min-w-0 flex-1 border-0 bg-transparent text-[12px] font-semibold text-white outline-none placeholder:text-lo',
            expanded ? 'block' : 'hidden',
          )}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onChange('');
              setOpen(false);
              e.currentTarget.blur();
            }
          }}
          onBlur={() => {
            if (!value) setOpen(false);
          }}
        />
        {expanded && value && (
          <button
            type="button"
            className="flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent text-lo transition-colors duration-150 hover:text-hi"
            aria-label="Clear search"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        )}
      </form>
    </div>
  );
}
